import type { Label, Locale } from "../texts";
import { formatText } from "../texts";
import { whenIn } from "../orgs/organizations";

// Datos y textos compartidos de Inteligencia artificial: lo que AiDisplayText hacía en el Platform Admin actual, con las
// mismas claves de platform.ui_label. Un código que no se conoce nunca se muestra crudo: sale «Desconocido».

export type Capability = "" | "TEXT" | "EMBEDDING" | "IMAGE_INPUT";

export type ChainRow = {
  priority: number;
  providerModelId: number;
  providerCode: string;
  providerModelCode: string;
  modelName: string;
  status: string;
  providerName: string;
};

export type RouteCandidate = {
  providerModelId: number;
  providerCode: string;
  providerName: string;
  providerModelCode: string;
  modelName: string;
  availability: string;
  protocolSupported: boolean;
  capabilities: string;
  protocolCode: string;
  priceIn: number | null;
  priceOut: number | null;
  priceCached: number | null;
  priceCurrency: string | null;
};

export type Feedback = { tone: "done" | "error" | "info" | "warning"; text: string };

/** Respuesta de error de los endpoints de IA: la clave del texto y, si corresponde, el campo. */
export type AiError = { messageKey?: string; field?: string | null; args?: (string | number)[] };

/** Las tres rutas que Bheli conoce; otra muestra su nombre del servidor o «Desconocido». */
export function routeLabel(L: Label, code: string, apiLabel?: string | null): string {
  switch (code) {
    case "TEXT_CHAT":
      return L("ROUTE_TEXT_CHAT");
    case "VISION_CHAT":
      return L("ROUTE_VISION_CHAT");
    case "EMBEDDING":
      return L("ROUTE_EMBEDDING");
    default:
      return apiLabel?.trim() || L("USAGE_UNKNOWN");
  }
}

/** Estado de un proveedor o de una conexión, con su etiqueta (C15). */
export function providerState(L: Label, status: string): { text: string; tone: "" | "accent" | "ochre" | "error" } {
  switch (status) {
    case "CONNECTED":
      return { text: L("AI_PROVIDER_CONNECTED"), tone: "accent" };
    case "CONNECTION_ERROR":
      return { text: L("AI_PROVIDER_ERROR"), tone: "error" };
    case "DISABLED":
      return { text: L("AI_PROVIDER_DISABLED"), tone: "" };
    case "NEEDS_KEY":
    case "NEEDS_VERIFICATION":
      return {
        text: L(status === "NEEDS_KEY" ? "AI_PROVIDER_NEEDS_KEY" : "AI_PROVIDER_NEEDS_VERIFICATION"),
        tone: "ochre",
      };
    default:
      return { text: L("AI_PROVIDER_NOT_CONFIGURED"), tone: "" };
  }
}

/** Configuración iniciada sin terminar: falta la clave, falta probarla o falló. */
export const isPending = (status: string) =>
  status === "NEEDS_KEY" || status === "NEEDS_VERIFICATION" || status === "CONNECTION_ERROR";

export function availability(L: Label, value: string): { text: string; tone: "accent" | "error" | "ochre" } {
  if (value === "AVAILABLE") return { text: L("MODELS_AVAILABLE"), tone: "accent" };
  if (value === "UNAVAILABLE") return { text: L("MODELS_UNAVAILABLE"), tone: "error" };
  return { text: L("MODELS_UNVERIFIED"), tone: "ochre" };
}

/** «1 modelo en catálogo» o «N modelos en catálogo». */
export const modelCount = (L: Label, count: number) =>
  count === 1 ? L("AI_MODEL_COUNT_ONE") : formatText(L("AI_MODEL_COUNT_MANY"), count);

/** Métrica con singular y plural. */
export const metric = (L: Label, one: string, many: string, count: number) =>
  count === 1 ? L(one) : formatText(L(many), count);

/** Fecha y hora cortas en el idioma de la persona, sin cambiar la zona. */
export function dateTime(locale: Locale, value: string | null | undefined, L: Label): string {
  if (!value) return L("USAGE_UNKNOWN");
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

const amount = (locale: Locale, L: Label, value: number | null | undefined) =>
  value == null
    ? L("CHAIN_NO_PRICE")
    : new Intl.NumberFormat(locale, { maximumFractionDigits: 6, minimumFractionDigits: 0 }).format(value);

/** Precio por dimensión; nunca dice «gratis» con datos que no lo demuestran. */
export function price(
  L: Label,
  locale: Locale,
  input: number | null | undefined,
  output: number | null | undefined,
  currency: string | null | undefined,
  options: { cached?: number | null; minimum?: boolean } = {},
): string {
  if (input == null && output == null && options.cached == null) return L("CHAIN_NO_PRICE");
  const text = formatText(
    L(options.minimum ? "AI_PRICE_MINIMUM_PAIR" : "AI_PRICE_PAIR"),
    amount(locale, L, input),
    amount(locale, L, output),
    currency?.trim() || L("CHAIN_NO_PRICE"),
  );
  return options.cached == null
    ? text
    : `${text} · ${formatText(L("AI_PRICE_CACHED"), amount(locale, L, options.cached))}`;
}

const extraCapabilities: Record<string, string> = {
  IMAGE_GENERATION: "NPA_AI_CAP_IMAGE_GENERATION",
  AUDIO_INPUT: "NPA_AI_CAP_AUDIO_INPUT",
  AUDIO_GENERATION: "NPA_AI_CAP_AUDIO_GENERATION",
  VIDEO_INPUT: "NPA_AI_CAP_VIDEO_INPUT",
  VIDEO_GENERATION: "NPA_AI_CAP_VIDEO_GENERATION",
};

/** Orden fijo de las capacidades en la lista y en el detalle (E48): las demás van después, en el orden que llegan. */
const capabilityOrder = ["TEXT", "IMAGE_INPUT", "TOOL_CALLING", "STRUCTURED_OUTPUT", "EMBEDDING"];
const capabilityRank = (code: string) => {
  const index = capabilityOrder.indexOf(code);
  return index < 0 ? capabilityOrder.length : index;
};

/**
 * Capacidades en palabras, una sola función para IA › Modelos y el detalle del modelo (E107): «Texto · Imágenes ·
 * Herramientas · Respuestas estructuradas · Embedding» en ese orden; códigos nuevos como «Desconocido».
 */
export function capabilityText(L: Label, codes: string): string {
  const names = codes
    .split(",")
    .map((code) => code.trim())
    .filter(Boolean)
    .sort((a, b) => capabilityRank(a) - capabilityRank(b))
    .map((code) =>
      code === "TEXT"
        ? L("MODELS_CAP_TEXT")
        : code === "IMAGE_INPUT"
          ? L("MODELS_CAP_IMAGES")
          : code === "TOOL_CALLING"
            ? L("MODELS_CAP_TOOLS")
            : code === "EMBEDDING"
              ? L("MODELS_CAP_EMBEDDING")
              : code === "STRUCTURED_OUTPUT"
                ? L("NPA_AI_CAP_STRUCTURED")
                : extraCapabilities[code]
                  ? L(extraCapabilities[code])
                  : L("USAGE_UNKNOWN"),
    );
  return [...new Set(names)].join(" · ") || "—";
}

/** Opciones del filtro de capacidad: las mismas cuatro del Platform Admin actual. */
export const capabilityOptions = (L: Label): { value: Capability; label: string }[] => [
  { value: "", label: L("AI_MODELS_ALL_CAPABILITIES") },
  { value: "TEXT", label: L("MODELS_CAP_TEXT") },
  { value: "EMBEDDING", label: L("MODELS_CAP_EMBEDDING") },
  { value: "IMAGE_INPUT", label: L("ROUTE_VISION_CHAT") },
];

export const normalizeCapability = (value: string | null): Capability =>
  value === "TEXT" || value === "EMBEDDING" || value === "IMAGE_INPUT" ? value : "";

/** Rutas donde el proveedor está por defecto: «Embeddings · Chat de texto» o «Sin asignaciones». */
export const assignedSummary = (L: Label, assigned: { routeCode: string }[]) =>
  assigned.length === 0 ? L("AI_ASSIGNED_NONE") : assigned.map((item) => routeLabel(L, item.routeCode)).join(" · ");

/** Estado de una ejecución o de un intento. */
export function executionStatus(L: Label, status: string): { text: string; tone: "" | "accent" | "error" } {
  switch (status) {
    case "SUCCESS":
      return { text: L("EXEC_SUCCESS"), tone: "accent" };
    case "ERROR":
      return { text: L("EXEC_ERROR"), tone: "error" };
    case "STARTED":
      return { text: L("EXEC_STARTED"), tone: "" };
    case "SKIPPED":
      return { text: L("EXEC_SKIPPED"), tone: "" };
    default:
      return { text: L("USAGE_UNKNOWN"), tone: "" };
  }
}

/** Tipo de operación legible. */
export function operationLabel(L: Label, type: string): string {
  const key = (
    {
      CHAT: "OPTYPE_CHAT",
      AGENT_TURN: "OPTYPE_AGENT_TURN",
      CONNECTION_TEST: "OPTYPE_CONNECTION_TEST",
      BENCHMARK: "OPTYPE_BENCHMARK",
      EMBEDDING: "OPTYPE_EMBEDDING",
    } as Record<string, string>
  )[type];
  return L(key ?? "USAGE_UNKNOWN");
}

/** Texto de un error de los endpoints de IA (sin respuesta: el genérico). */
export const errorText = (L: Label, body: AiError | null | undefined) =>
  formatText(L(body?.messageKey || "ADMIN_ERR_GENERIC"), ...(body?.args ?? []));

/** Clase de la etiqueta C15 para un tono. */
export const tagClass = (tone: string) => `tm-tag${tone ? ` ${tone === "error" ? "npa-tag-error" : tone}` : ""}`;

/** Un número entero en el idioma de la persona. */
export const number = (locale: Locale, value: number | null | undefined) =>
  value == null ? "—" : new Intl.NumberFormat(locale).format(value);

// ---- R2 · textos y formatos de IA con el catálogo 1.14.7 (C24, C55, D12) ----

/** Singular o plural con su número formateado (E18): «1 modelo», «24 modelos». */
export const plural = (L: Label, locale: Locale, count: number, one: string, many: string) =>
  formatText(L(count === 1 ? one : many), count.toLocaleString(locale));

/**
 * Modelos de un proveedor con la regla única de AI Management (E107): «45 usables de 52», «1 usable de 1», o «0 modelos»
 * si no tiene ninguno. Los mismos dos números que las píldoras «Usables · Todos» de su pestaña Modelos; lo dicen igual
 * su ficha, la lista de Proveedores e IA › Resumen.
 */
export const usableOf = (L: Label, locale: Locale, usable: number, all: number) =>
  all === 0
    ? plural(L, locale, 0, "ADM_N_MODELS_ONE", "ADM_N_MODELS")
    : formatText(
        L(usable === 1 ? "ADM_N_USABLE_OF_ONE" : "ADM_N_USABLE_OF"),
        usable.toLocaleString(locale),
        all.toLocaleString(locale),
      );

/** Nombre de una ruta con las claves del Admin; una ruta que Bheli no conoce, «Desconocido». */
export const routeName = (L: Label, code: string | null | undefined) =>
  code === "TEXT_CHAT"
    ? L("ADM_ROUTE_TEXT")
    : code === "VISION_CHAT"
      ? L("ADM_ROUTE_VISION")
      : code === "EMBEDDING"
        ? L("ADM_ROUTE_EMBEDDING")
        : L("USAGE_UNKNOWN");

export type Tone = "ok" | "warn" | "fail" | "off";

/** C55 · estado de un proveedor o de una clave en palabras. */
export function connectionTone(L: Label, state: string): { tone: Tone; text: string } {
  switch (state) {
    case "CONNECTED":
      return { tone: "ok", text: L("ADM_STATE_OK") };
    case "CONNECTION_ERROR":
      return { tone: "fail", text: L("ADM_STATE_FAILED") };
    case "NEEDS_KEY":
      return { tone: "warn", text: L("ADM_STATE_REVIEW") };
    case "NEEDS_VERIFICATION":
      return { tone: "warn", text: L("ADM_AI_STATE_VERIFY") };
    case "DISABLED":
      return { tone: "off", text: L("ADM_AI_STATE_DISABLED") };
    default:
      // Un proveedor no se «conecta»: sin API key activa (decisión del dueño, 2026-10-30).
      return { tone: "off", text: L("ADM_AI_STATE_NO_KEY") };
  }
}

/**
 * C55 · disponibilidad de un modelo. «Agregado a mano · sin probar» (script 166) es un modelo que la sincronización no
 * cubre y que aún no respondió a una prueba real; «Sin verificar» queda para lo que se sincroniza y aún no se vio.
 */
export function availabilityTone(L: Label, value: string | null | undefined): { tone: Tone; text: string } {
  if (value === "AVAILABLE") return { tone: "ok", text: L("ADM_AI_AVAILABLE") };
  if (value === "UNAVAILABLE") return { tone: "fail", text: L("ADM_AI_UNAVAILABLE") };
  // Un estado, una palabra (C55 1.14.12, E125): «agregado a mano» es otro dato y no va en esta etiqueta.
  return { tone: "warn", text: L("ADM_AI_UNVERIFIED") };
}

/** Cómo y cuándo se supo la disponibilidad de una vía (ficha del modelo): probada, vista en el catálogo o por probar. */
export type PathCheck = {
  availability: string;
  manualUntested: boolean;
  /** Agregado con «Modelo manual» (no lo trae la sincronización): otro dato que la disponibilidad (D2). */
  addedManually?: boolean;
  seenInCatalog: boolean;
  catalogSyncedAt: string | null;
  availabilityCheckedAt: string | null;
  availabilityCheckSource: string | null;
};

/** «Probado hoy 09:50», «Comprobado en uso ayer 22:10», «Visto en el catálogo hoy 08:00» o qué hacer para probarlo. */
export function pathCheckText(L: Label, locale: Locale, path: PathCheck): string | null {
  if (path.manualUntested) return L("ADM_AI_MANUAL_UNTESTED_LINE");
  if (path.seenInCatalog && path.catalogSyncedAt)
    return formatText(L("ADM_AI_CHECKED_CATALOG"), when(L, locale, path.catalogSyncedAt, true));
  if (path.availabilityCheckedAt) {
    const key = path.availabilityCheckSource === "CALL" ? "ADM_AI_CHECKED_CALL" : "ADM_AI_CHECKED_KEY_TEST";
    return formatText(L(key), when(L, locale, path.availabilityCheckedAt, true));
  }
  return null;
}

/**
 * RC1 · K3 (AF5, E40): «Intentos» de una ejecución son los intentos registrados. Una ejecución que respondió tiene al
 * menos uno: si respondió y no hay ninguno registrado, el dato falta y se muestra «—», nunca 0.
 */
export function attemptsMissing(status: string, count: number): boolean {
  return status === "SUCCESS" && count === 0;
}

export function attemptsText(locale: Locale, status: string, count: number): string {
  return attemptsMissing(status, count) ? "—" : count.toLocaleString(locale);
}

/** C55 · resultado de una ejecución o de un intento. */
export function executionTone(L: Label, status: string): { tone: Tone; text: string } {
  switch (status) {
    case "SUCCESS":
      return { tone: "ok", text: L("ADM_AI_EXEC_SUCCESS") };
    case "ERROR":
      return { tone: "fail", text: L("ADM_AI_EXEC_ERROR") };
    case "STARTED":
      return { tone: "warn", text: L("ADM_AI_EXEC_STARTED") };
    default:
      return { tone: "off", text: L("ADM_AI_EXEC_SKIPPED") };
  }
}

/** Tipo de una ejecución con los nombres del filtro «Tipo» (una sola definición, E52). */
export function operationName(L: Label, type: string): string {
  switch (type) {
    case "CHAT":
    case "AGENT_TURN":
    case "RAG_GENERATION":
      return L("ADM_AI_TYPE_CONVERSATION");
    case "CONNECTION_TEST":
      return L("ADM_AI_TYPE_TEST");
    case "EMBEDDING":
      return L("ADM_AI_TYPE_EMBEDDING");
    case "BENCHMARK":
      return L("ADM_AI_TYPE_BENCHMARK");
    default:
      return L("USAGE_UNKNOWN");
  }
}

/**
 * D12 · C24 1.14.8 · fechas de IA en la hora de Lima con el formateador único del Admin (AR7): «Hoy 10:42», «Ayer 22:10»,
 * «Miércoles 12:18», «28 de octubre, 12:18» y el año solo si no es este (nunca «28 oct.»).
 */
export function when(L: Label, locale: Locale, iso: string | null | undefined, inline = false): string {
  return whenIn(L, locale, iso, "America/Lima", inline, true);
}

/** Decimales de un monto (C24 1.14.11): mínimo 2 y máximo 4, sin ceros de más («0.028», «0.50», «15.00»). */
const moneyDigits = { minimumFractionDigits: 2, maximumFractionDigits: 4 } as const;

/** Dinero con su moneda, el único formateador del Admin; sin precio, «—» (nunca 0, §7.1). Dólares como «US$ 0.028». */
export function money(locale: Locale, value: number | null | undefined, currency: string | null | undefined): string {
  if (value == null) return "—";
  const code = (currency ?? "USD").trim().toUpperCase() || "USD";
  const amount = new Intl.NumberFormat(locale, moneyDigits).format(value);
  return `${code === "USD" ? "US$" : code} ${amount}`;
}

/**
 * C24 1.14.11 · el precio completo de una vía en una línea: «Entrada US$ 0.28 · En caché US$ 0.028 · Salida US$ 0.42 por
 * 1M», con «—» en la parte que falta. Sin ninguna de las tres, null: quien llama dice «Sin precio».
 */
export function priceLine(
  L: Label,
  locale: Locale,
  price: { input: number | null | undefined; cached: number | null | undefined; output: number | null | undefined },
  currency: string | null | undefined,
): string | null {
  if (price.input == null && price.cached == null && price.output == null) return null;
  // H12: «US$ {salida} por 1M» no se parte (espacios duros): baja entera, nunca «por 1M» sola en otra línea.
  const template = L("ADM_AI_PRICE_FULL");
  const at = template.indexOf("{2}");
  const glued = at < 0 ? template : template.slice(0, at + 3) + template.slice(at + 3).replace(/ /g, "\u00a0");
  return formatText(
    glued,
    money(locale, price.input, currency),
    money(locale, price.cached, currency),
    money(locale, price.output, currency).replace(/ /g, "\u00a0"),
  );
}

/** Precio de entrada / salida por 1M con una sola moneda («US$ 0.40 / 1.60»); sin precio, «—». */
export function pricePair(
  locale: Locale,
  input: number | null | undefined,
  output: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (input == null && output == null) return "—";
  const first = input == null ? "—" : money(locale, input, currency);
  const second = output == null ? "—" : new Intl.NumberFormat(locale, moneyDigits).format(output);
  return `${first} / ${second}`;
}

/** Números grandes cortos para una tabla («11.2 M»); hasta 9 999 tal cual. */
export const compact = (locale: Locale, value: number) =>
  value < 10000
    ? value.toLocaleString(locale)
    : new Intl.NumberFormat(locale, { notation: "compact", maximumFractionDigits: 1 }).format(value);

/** Tiempo de una ejecución («1.4 s» o «640 ms»). */
export const duration = (L: Label, locale: Locale, ms: number | null | undefined) =>
  ms == null
    ? "—"
    : ms < 1000
      ? formatText(L("ADM_AI_MS"), ms.toLocaleString(locale))
      : formatText(
          L("ADM_AI_SECONDS"),
          (ms / 1000).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
        );
